import "../../style.css";
import util from "../../styles/utilities.module.css";
// import { useNavigate } from "react-router-dom";
import classes from "./ToolSelection.module.css";
import SelectToolActionButton from "../UI/SelectToolActionButton/SelectToolActionButton";
import ProgressBarWithCircles from "../Layout/ProgressBar";
import {
  selectFormType,
  setFormData,
  setFormType,
  setReadingPassage,
} from "../../slices/form";
import { setDefaultValues as setGradingDefaultValues } from "../../slices/gradingForm";
import { setDefaultValues as setFormDefaultValues } from "../../slices/form";
import { useSelector, useDispatch } from "react-redux";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Examples from "../CriteriaSubmission/examples";
// import { useManageSubscriptionMutation } from "../../slices/subscriptionApi";
import {
  IN_ACTIVE,
  ACTIVE,
  TRIAL,
  EXPIRED,
  gradingToolButtonLabelMapper,
  gradingToolColorMapper,
  gradingToolButtonColorMapper
} from "../../constants";
import { Link, useNavigate } from "react-router-dom";
import CityYearStats from "../UI/CityYearStats/CityYearStats";
import { useGetCredentialingDashboardQuery, useGetBadgeMediaQuery } from "../../slices/credentialingApi";
import { useFetchPublicConfigQuery } from '../../slices/authApi';
import { setStoredBadgeSummaries, upsertBadgeMedia } from "../../utils/credentialingStorage";
import useAutoCollectBadges from "../../hooks/useAutoCollectBadges";
import BadgeDetailsModal from "../badges/BadgeDetailsModal";

const EARNED_BADGE_STORAGE_KEY = 'credentialingEarnedBadgeIds';

const BadgeMediaPrefetch = ({ badgeId }) => {
  const { data } = useGetBadgeMediaQuery(badgeId, {
    skip: !badgeId,
  });

  const media = data?.data || data || {};
  const iconUrl = media.icon_url || media.iconUrl || media.icon;
  const heroUrl = media.image_url || media.large_url || media.image || media.hero_url;

  useEffect(() => {
    if (!badgeId) {
      return;
    }
    if (!iconUrl && !heroUrl) {
      return;
    }

    upsertBadgeMedia(badgeId, {
      iconUrl: iconUrl || null,
      heroUrl: heroUrl || null,
    });
  }, [badgeId, iconUrl, heroUrl]);

  return null;
};

const BadgeCelebrationBanner = ({ badge, extraBadges, isVisible, onClose, onShare }) => {
  const badgeId = badge?.badge?.id;
  const badgeName = badge?.badge?.name || 'Badge';
  const { data } = useGetBadgeMediaQuery(badgeId, { skip: !badgeId });

  const media = data?.data || data || {};
  const heroUrl = media.image_url || media.large_url || media.image || media.hero_url;
  const iconUrl = media.icon_url || media.iconUrl || media.icon;
  const extraCount = Array.isArray(extraBadges) ? extraBadges.length : 0;

  useEffect(() => {
    if (!badgeId) {
      return;
    }
    if (!iconUrl && !heroUrl) {
      return;
    }

    upsertBadgeMedia(badgeId, {
      iconUrl: iconUrl || null,
      heroUrl: heroUrl || null,
    });
  }, [badgeId, iconUrl, heroUrl]);

  return (
    <div
      className={`${classes.badgeCelebrationBanner} ${isVisible ? classes.badgeCelebrationBannerVisible : ''}`}
      role="status"
      aria-live="polite"
    >
      <div className={classes.badgeCelebrationArtwork}>
        {heroUrl ? (
          <img
            src={heroUrl}
            alt={`${badgeName} badge artwork`}
            className={classes.badgeCelebrationImage}
          />
        ) : iconUrl ? (
          <img
            src={iconUrl}
            alt={`${badgeName} badge artwork`}
            className={classes.badgeCelebrationImage}
          />
        ) : (
          <span
            role="img"
            aria-label={`${badgeName} badge artwork placeholder`}
            className={classes.badgeCelebrationEmoji}
          >
            🏅
          </span>
        )}
      </div>
      <div className={classes.badgeCelebrationText}>
        <strong className={classes.badgeCelebrationTitle}>Badge Earned!</strong>
        <p className={classes.badgeCelebrationMessage}>
          {`You just earned the ${badgeName} badge${extraCount ? ` and ${extraCount} more!` : '!'}`}
        </p>
      </div>
      <div className={classes.badgeCelebrationActions}>
        <button
          type="button"
          onClick={() => onShare?.(badgeId)}
          className={classes.badgeCelebrationShare}
        >
          Share Out
        </button>
        <button
          type="button"
          onClick={onClose}
          className={classes.badgeCelebrationDismiss}
          aria-label="Dismiss badge celebration"
        >
          Close
        </button>
      </div>
    </div>
  );
};

const ToolSelection = () => {
  const endRef = useRef(null);
  const formType = useSelector(selectFormType);
  // Removed CarouselPopUp modal state (selectedWindow, selectedCarouselElement) – selection now navigates directly
  const [exampleSelected, setExampleSelected] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showBadgeCelebration, setShowBadgeCelebration] = useState(false);
  const [recentlyEarnedBadge, setRecentlyEarnedBadge] = useState(null);
  const [additionalEarnedBadges, setAdditionalEarnedBadges] = useState([]);
  const [shareModalBadge, setShareModalBadge] = useState(null);
  const [shareModalDigitalBadge, setShareModalDigitalBadge] = useState(null);
  const dispatch = useDispatch();

  const navigate = useNavigate();
  // const [subscriptionUrl, setSubscriptionUrl] = useState("/");
  // const [manageSubscription, { isLoading }] = useManageSubscriptionMutation();

  // Credentialing dashboard query - removed since tile is removed for now
  // const { data: credentialingData } = useGetCredentialingDashboardQuery();

  const showGradingTool =
    userInfo?.user_options?.grading_tool_available || false;
  const showWorksheetTool = userInfo?.user_options?.worksheet_criteria_selection || false;
  const showTextLevelingTool = userInfo?.user_options?.text_leveling_tool_available || false;

  const subscriptionStatus = userInfo?.subscription_status || TRIAL;

  const userGroup = userInfo?.user_options?.user_group || "";
  const gradingToolButtonLabel =
    gradingToolButtonLabelMapper[subscriptionStatus];
  const gradingToolColor =
    gradingToolColorMapper[subscriptionStatus];
  const gradingToolButtonColor =
    gradingToolButtonColorMapper[subscriptionStatus];

  // Fetch public config values (strings). Example key: 'show_leaderboard' -> 'true'|'false'
  const { data: publicConfigData } = useFetchPublicConfigQuery();
  const publicConfig = publicConfigData?.config || {};
  const showLeaderboard = (() => {
    const v = publicConfig?.show_leaderboard;
    if (v === undefined || v === null) return true; // default show when missing
    if (typeof v === 'boolean') return v;
    const s = String(v).toLowerCase();
    return ["1","true","t","yes","y","on"].includes(s);
  })();

  const { data: credentialingDashboardData, isFetching: isFetchingCredentialing } = useGetCredentialingDashboardQuery();

  const credentialingBadges = useMemo(
    () => (credentialingDashboardData?.data?.badges || []),
    [credentialingDashboardData]
  );

  const earnedBadges = useMemo(
    () => credentialingBadges.filter((badgeEntry) => badgeEntry?.progress?.is_earned),
    [credentialingBadges]
  );

  const earnedBadgeSummaries = useMemo(
    () => earnedBadges
      .map((badgeEntry) => ({
        id: badgeEntry?.badge?.id,
        name: badgeEntry?.badge?.name,
      }))
      .filter((item) => !!item.id),
    [earnedBadges]
  );

  useEffect(() => {
    if (earnedBadgeSummaries.length === 0) {
      setStoredBadgeSummaries([]);
      return;
    }
    setStoredBadgeSummaries(earnedBadgeSummaries);
  }, [earnedBadgeSummaries]);

  useAutoCollectBadges(credentialingBadges);

  useEffect(() => {
    if (isFetchingCredentialing) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const badgeIds = earnedBadgeSummaries.map((item) => item.id);

    const storedRaw = window.localStorage.getItem(EARNED_BADGE_STORAGE_KEY);
    const hasStoredBefore = storedRaw !== null;

    let storedIds = [];
    try {
      storedIds = storedRaw ? JSON.parse(storedRaw) : [];
      if (!Array.isArray(storedIds)) {
        storedIds = [];
      }
    } catch (error) {
      storedIds = [];
    }

    const storedSet = new Set(storedIds);
    const newBadges = earnedBadges.filter((badgeEntry) => {
      const badgeId = badgeEntry?.badge?.id;
      return badgeId && !storedSet.has(badgeId);
    });

    if (newBadges.length > 0 && hasStoredBefore) {
      setRecentlyEarnedBadge(newBadges[0]);
      setAdditionalEarnedBadges(newBadges.slice(1));
      setShowBadgeCelebration(true);
    } else {
      setAdditionalEarnedBadges([]);
    }

    if (badgeIds.length > 0) {
      const merged = Array.from(new Set([...storedIds, ...badgeIds]));
      window.localStorage.setItem(EARNED_BADGE_STORAGE_KEY, JSON.stringify(merged));
    } else if (!storedIds.length) {
      window.localStorage.setItem(EARNED_BADGE_STORAGE_KEY, JSON.stringify([]));
    }
  }, [earnedBadges, earnedBadgeSummaries, isFetchingCredentialing]);

  const dismissCelebration = () => {
    setShowBadgeCelebration(false);
  };

  const closeShareModal = () => {
    setShareModalBadge(null);
    setShareModalDigitalBadge(null);
  };

  const formHandler = (formType, data) => {
    dispatch(setFormType({ formType }));
    dispatch(setFormData(data));
    dispatch(setReadingPassage(""));
  };

  const assessmentClickHandler = (event) => {
    event.preventDefault();
    let skills = {
      knowledge: true,
      analysis: true,
      comprehension: true,
      application: true,
      fluency: true,
      concept: true,
      reasoning: true,
    };
    formHandler("assessment", {
      subject: localStorage.getItem("subject") || "",
      grade: localStorage.getItem("grade") || "",
      text: "",
      text_files: [],
      text_files_qty: 0,
      primary_source: false,
      difficulty_level: "",
      questions: {
        mc_quantity: 3,
        ff_quantity: 3,
        tf_quantity: 3,
        fb_quantity: 3,
        mc_options: 4,
        skills: skills,
      },
      genre: "",
      type: "",
      assessment_instructions: "",
      readingPassage: "",
    });
  };

  const readingClickHandler = (event) => {
    event.preventDefault();
    dispatch(setFormDefaultValues());
    formHandler("reading", {
      difficulty_level: "Medium",
      subject: localStorage.getItem("subject") || "",
      grade: localStorage.getItem("grade") || "",
      genre: "",
      length: "",
      show_text: "Open book",
      text: "",
      text_instructions: "",
      text_instructions_files: [],
      assessment_instructions: "",
      questions: {
        mc_quantity: 3,
        ff_quantity: 3,
        tf_quantity: 3,
        fb_quantity: 3,
        mc_options: 4,
        skills: {
          theme: true,
          analysis: true,
          structure: true,
          tone: true,
          vocabulary: true,
        },
      },
      show_worksheet_tool: showWorksheetTool,
    });
  };

  const gradingClickHandler = async (event) => {
    dispatch(setFormType({ formType: "grading" }));
    if ([ACTIVE, TRIAL].includes(subscriptionStatus)) {
      event.preventDefault();
      dispatch(setGradingDefaultValues());
    }
  };

  const sippsClickHandler = (event) => {
    event.preventDefault();
    dispatch(setFormDefaultValues());
    formHandler("sipps", {});
  };

  const textLevelingClickHandler = (event) => {
    event.preventDefault();
    formHandler('textLeveling', {});
    // Navigation to /textLeveling is now handled in the SelectToolActionButton component
  };

  const credentialingClickHandler = (event) => {
    event.preventDefault();
    navigate('/credentialing');
  };

  const badgesClickHandler = (event) => {
    event.preventDefault();
    navigate('/badges');
  };

  const navigateToNextPageHandler = async () => {
    if ([IN_ACTIVE, EXPIRED].includes(subscriptionStatus)) {
      // window.open(subscriptionUrl, "_self");
    } else {
      navigate("/grading/assignment");
    }
  };

  const cardHeadingText = (text) => {
    return (
      <div className={classes.uploadText}>
        <span style={{ fontSize: "1.0em", fontWeight: "bold" }}>{text}</span>
        <br />
      </div>
    );
  };

  // const isTileSelected = ["assessment", "reading"].includes(formType);

  useEffect(() => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const userData = JSON.parse(userStr);
        if (userData && userData.userData) {
          setUserInfo(userData.userData);
        }
      }
    } catch (error) {
      console.warn("Failed to parse user data from localStorage:", error);
      // Continue with default state if user data is malformed
    }

    //   const fetchSubscriptionUrl = async () => {
    //     const { url = "/" } = await manageSubscription().unwrap();
    //     setSubscriptionUrl(url);
    //   };
    //   fetchSubscriptionUrl();
  }, []);

  const subscriptionEndDate = new Date(
    userInfo?.subscription_end_date || new Date()
  );
  const currentDate = new Date();
  const timeDiff = subscriptionEndDate.getTime() - currentDate.getTime();
  const daysLeft = Math.round(timeDiff / (1000 * 3600 * 24));

  const subscriptionStatusTextMapper = {
    [IN_ACTIVE]: (
      <p style={{ fontSize: "larger", textAlign: "center" }}>
        Interested in access to our new grading tool? <a href="https://docs.google.com/forms/d/e/1FAIpQLSclIJSVhk9UDazMwfdtLutiKwXTkcgPPbLx0bUTZYsb_uwqUg/viewform" target="_blank" rel="noopener noreferrer">Sign up here</a>
        &nbsp;for free early access to the beta (limited spots).
      </p>
    ),
    [TRIAL]: (
      <p>
        {`You're currently on your free trial of premium, you have ${daysLeft} day${daysLeft === 1 ? "s" : ""
          } left. `}
        <Link to={""/*subscriptionUrl*/} target="_self">{` Upgrade.`}</Link>
      </p>
    ),
    [ACTIVE]: "   ",
    [EXPIRED]: (
      <p>
        {`You're currently on the free plan. `}
        <Link to={""/*subscriptionUrl*/} target="_self">{` Upgrade.`}</Link>
      </p>
    ),
  };

  const defaultShareBadgeId = recentlyEarnedBadge?.badge?.id || earnedBadgeSummaries[0]?.id;

  const openShareModal = (badgeId) => {
    const targetId = badgeId || defaultShareBadgeId;
    if (!targetId) {
      return;
    }

    const badgeEntry = earnedBadges.find((entry) => entry?.badge?.id === targetId && entry?.progress?.is_earned);

    if (!badgeEntry) {
      navigate('/credentialing', { state: { focusBadgeId: targetId } });
      return;
    }

    const progress = badgeEntry.progress || {};

    if (!progress.credential_id) {
      navigate('/credentialing', { state: { focusBadgeId: targetId } });
      return;
    }

    const badgeForModal = {
      badge_id: badgeEntry.badge.id,
      badge_name: badgeEntry.badge.name,
      badge_type: badgeEntry.badge.badge_type,
      description: badgeEntry.badge.description,
    };

    const digitalBadgeForModal = {
      badge_id: badgeEntry.badge.id,
      badge_name: badgeEntry.badge.name,
      badge_type: badgeEntry.badge.badge_type,
      description: badgeEntry.badge.description,
      credential_id: progress.credential_id,
      verification_url: progress.verification_url,
      earned_at: progress.earned_at,
      issued_date: progress.earned_at,
      is_revoked: progress.is_revoked,
      digital_badge_data: progress.digital_badge_data,
      badge_data: progress.digital_badge_data,
    };

    setShareModalBadge(badgeForModal);
    setShareModalDigitalBadge(digitalBadgeForModal);
  };

  return (
    <div className="page-margin">
      {recentlyEarnedBadge && (
        <BadgeCelebrationBanner
          badge={recentlyEarnedBadge}
          extraBadges={additionalEarnedBadges}
          isVisible={showBadgeCelebration}
          onClose={dismissCelebration}
          onShare={openShareModal}
        />
      )}

      {earnedBadgeSummaries.map(({ id }) => (
        <BadgeMediaPrefetch key={id} badgeId={id} />
      ))}

      <section className={classes.mainContent} aria-label="Tool selection content">
        <p style={{ fontSize: "larger", textAlign: "center" }}>
          <strong style={{ fontSize: "inherit" }}>
            Charting the next chapter in teaching.
          </strong>
          <br />
          Create and grade assignments in minutes. Teach smarter, faster. Let's
          dive in!
        </p>
        <div style={{ paddingTop: "60px" }} />

        <ProgressBarWithCircles progress={0} />
        <p style={{ fontSize: "larger", textAlign: "center" }}>
          <br />
          Let's get started! First, please tell us what you'd like to do.
        </p>
        <div className={classes["input-container"]}>
          <br />
          <br />
          <div className={classes.actionButtonGroup}>
            <SelectToolActionButton
              text={cardHeadingText(
                "Create questions for a text or list of terms you upload"
              )}
              isSelected={formType === "assessment"}
              handleOnClick={assessmentClickHandler}
              cardIndex={0}
            />
            <SelectToolActionButton
              text={cardHeadingText(
                "Create an original text along with a set of questions"
              )}
              isSelected={formType === "reading"}
              handleOnClick={readingClickHandler}
              cardIndex={1}
            />
            {showGradingTool ? (
              <SelectToolActionButton
                text={cardHeadingText("Get help grading a paper")}
                isSelected={formType === "grading"}
                handleOnClick={gradingClickHandler}
                navigateToNextPageHandler={navigateToNextPageHandler}
                cardIndex={2}
                showRibbon={false}
                buttonLabel={gradingToolButtonLabel}
                toolColor={gradingToolColor}
                buttonColor={gradingToolButtonColor}
              />
            ) : null}
            {showWorksheetTool && (
              <SelectToolActionButton
                text={cardHeadingText(
                  "Create a worksheet for your ELA classes"
                )}
                isSelected={formType === "sipps"}
                handleOnClick={sippsClickHandler}
                cardIndex={3}
              />
            )}
            {showTextLevelingTool && (
              <SelectToolActionButton
                text={cardHeadingText(
                  "Adjust text complexity to students' abilities"
                )}
                isSelected={formType === "textLeveling"}
                handleOnClick={textLevelingClickHandler}
                cardIndex={4}
              />
            )}
            {/**
             * Credentialing tile temporarily removed per request (Aug 31 2025).
             * Keeping code commented out so it can be easily restored in the future.
             * Replacement: a header link labelled "Credentialing" beside the home icon.
             */}
            {/* <SelectToolActionButton
                text={cardHeadingText(
                  "Earn Your ClassGenius Credentials"
                )}
                isSelected={formType === "training"}
                handleOnClick={credentialingClickHandler}
                cardIndex={5}
                trainingData={credentialingData?.data}
              />
             <SelectToolActionButton
              text={cardHeadingText(
                "View & Share Your Digital Badges"
              )}
              isSelected={formType === "badges"}
              handleOnClick={badgesClickHandler}
              cardIndex={6}
            /> */}

          </div>
        </div>
        <div>
          {showGradingTool ? (
            <p
              style={{
                fontSize: "14px",
                margin: "5px 0px",
              }}
            >
              {subscriptionStatusTextMapper[subscriptionStatus]}
            </p>
          ) : null}
        </div>

        <div>
          <Examples
            scrollToRef={endRef}
            setExampleSelected={setExampleSelected}
            exampleSelected={exampleSelected}
            userGroup={userGroup}
            showWorksheetTool={showWorksheetTool}
          />
        </div>
        {userGroup?.startsWith("cy-") && showLeaderboard && (
          <div>
            <CityYearStats />
          </div>
        )}
      </section>
      <BadgeDetailsModal
        badge={shareModalBadge}
        digitalBadge={shareModalDigitalBadge}
        isOpen={!!shareModalBadge}
        onClose={closeShareModal}
      />
    </div>
  );
};

export default ToolSelection;
